import {
  QueryClient,
  UseQueryOptions,
  UseMutationOptions,
  DefaultOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

/** HTTP status of a failed request, however the error was surfaced.
 *  Axios errors carry `response.status`; the driverApi interceptors rethrow
 *  plain Errors with a `status` property instead, so check both. */
const statusOf = (error: unknown): number | undefined =>
  (error as AxiosError)?.response?.status ??
  (error as { status?: number })?.status;

/**
 * Retry transport failures only. A 4xx is the server's considered answer — an
 * expired token or a missing permission does not become valid by asking again
 * a second later, so retrying one only doubles the request volume.
 *
 * This mattered in production: a driver with a stale token generated ~30 failed
 * requests in 13 seconds, because this retry multiplied with the separate 401
 * retry in driverApi's response interceptor.
 *
 * Exported so it can be tested directly — the test suite mocks out QueryClient,
 * so the policy is not reachable through getDefaultOptions().
 */
export const shouldRetryQuery = (
  failureCount: number,
  error: unknown
): boolean => {
  const status = statusOf(error);
  if (status !== undefined && status >= 400 && status < 500) return false;
  return failureCount < 1;
};

const queryConfig: DefaultOptions = {
  queries: {
    refetchOnWindowFocus: true,
    retry: shouldRetryQuery,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 30 * 1000, // Refetch every 30 seconds for real-time updates
    /**
     * Poll only while the driver is actually looking at the app.
     *
     * The mobile apps are WebView shells around this bundle, so "backgrounded
     * on a phone" is the normal resting state — polling through it meant every
     * installed app hammered the API around the clock, and a single stale token
     * produced 401s all day rather than until the driver next opened the app.
     *
     * Safe because polling is not how drivers learn about work: assigning a
     * call sends both an SMS and a OneSignal push (call.service.ts,
     * sendNewCallNotification). Opening the app refetches on mount and on
     * focus, so nothing is missed on return.
     */
    refetchIntervalInBackground: false,
    refetchOnMount: true, // Always refetch on mount
    refetchOnReconnect: true, // Refetch when network reconnects
  },
};

export const queryClient = new QueryClient({ defaultOptions: queryConfig });

/**
 * Reset all queries in the cache.
 * Use when switching active context (e.g., company) to ensure stale data doesn't persist.
 */
export async function resetAllQueries(): Promise<void> {
  await queryClient.resetQueries();
}

type ExtractFnReturnType<FnType extends (...args: any) => any> = Awaited<
  ReturnType<FnType>
>;

export type QueryConfig<QueryFnType extends (...args: any) => any> = Omit<
  UseQueryOptions<ExtractFnReturnType<QueryFnType>>,
  'queryKey' | 'queryFn'
>;

export type MutationConfig<MutationFnType extends (...args: any) => any> = UseMutationOptions<
  ExtractFnReturnType<MutationFnType>,
  AxiosError,
  Parameters<MutationFnType>[0]
>; 